import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Percent, ShieldCheck, Info } from "lucide-react";
import { apiConfigComercial } from "../../lib/marketplaceApi";

const soles = (n) => `S/ ${Number(n || 0).toFixed(2)}`;

// Colores por plan (según spec): Básica verde, Pro azul, Elite morado.
const PLAN_META = {
  basico: { label: "Básica", tag: "Gratis", dot: "#16A34A", tint: "#DCFCE7", text: "#15803D" },
  pro: { label: "Pro", tag: "Beneficio PRO", dot: "#2563EB", tint: "#DBEAFE", text: "#1D4ED8" },
  elite: { label: "Elite", tag: "Beneficio Elite", dot: "#7C3AED", tint: "#EDE9FE", text: "#6D28D9" },
};

/**
 * Resumen de comisión en tiempo real para los formularios de publicación.
 * Lee la configuración comercial dinámica desde la BD (comisiones + precio mínimo).
 */
export default function ComisionResumen({ precio, gratuito = false }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      apiConfigComercial().then((d) => { if (vivo) setCfg(d); }).catch(() => {});
    };
    cargar();
    // Revalida la config comercial al volver a la pestaña / recuperar foco,
    // para reflejar cambios del Administrador sin recargar (no hay polling).
    const onFocus = () => cargar();
    const onVisible = () => { if (document.visibilityState === "visible") cargar(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      vivo = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!cfg) return null;

  const plan = cfg.mi_plan || "basico";
  const meta = PLAN_META[plan] || PLAN_META.basico;
  const pct = Number(cfg.mi_comision_pct || 0);
  const precioNum = Number(precio || 0);
  const esGratis = gratuito || precioNum <= 0;
  const comision = esGratis ? 0 : Number((precioNum * pct / 100).toFixed(2));
  const neto = esGratis ? 0 : Number((precioNum - comision).toFixed(2));
  const bajoMinimo = !esGratis && cfg.minimum_sale_price > 0 && precioNum < cfg.minimum_sale_price;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid="comision-resumen">
      {/* Comisión según membresía */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: meta.tint, color: meta.text }}>
            <Percent size={18} />
          </span>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Comisión ConstruLink</p>
            <p className="text-lg font-extrabold leading-none" style={{ color: meta.text }} data-testid="cr-comision-pct">{pct}%</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: meta.tint, color: meta.text }} data-testid="cr-plan">
          <ShieldCheck size={13} /> Membresía {meta.label} {plan === "basico" ? "(Gratis)" : ""}
        </span>
      </div>

      {/* Aviso de precio mínimo */}
      {bajoMinimo && (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-brand-red" data-testid="cr-min-alerta">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>El precio mínimo permitido para publicar en ConstruLink Marketplace es de <b>{soles(cfg.minimum_sale_price)}</b>. Ingrese un valor igual o superior.</span>
        </div>
      )}

      {/* Resumen de la venta */}
      <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100" data-testid="cr-resumen-venta">
        <Row label="Precio de venta" value={esGratis ? "Gratis" : soles(precioNum)} />
        <Row label={`Comisión ConstruLink (${pct}%)`} value={esGratis ? soles(0) : `- ${soles(comision)}`} color="#DC2626" />
        <Row label="Monto estimado a recibir" value={soles(neto)} strong color={meta.text} testid="cr-neto" />
      </div>
      <p className="text-[11px] text-slate-400 -mt-1">No incluye la comisión de Mercado Pago, que depende del método de pago del comprador.</p>

      {/* CTA membresía */}
      {plan === "basico" ? (
        <Link to="/planes" data-testid="cr-hazte-miembro" className="flex items-center justify-center gap-2 w-full rounded-xl bg-brand-blue text-white font-bold py-2.5 text-sm hover:brightness-110 transition">
          <Rocket size={16} /> Hazte Miembro y paga menos comisión
        </Link>
      ) : (
        <div className="rounded-xl px-3 py-2 text-sm font-semibold text-center" style={{ backgroundColor: meta.tint, color: meta.text }} data-testid="cr-beneficio-msg">
          Actualmente eres miembro {meta.label}. Tu comisión es de solo {pct}%.
        </div>
      )}

      {/* Comparativo de comisiones */}
      <div data-testid="cr-comparativo">
        <p className="text-xs font-semibold text-slate-500 mb-2">Comisiones actuales</p>
        <div className="grid grid-cols-3 gap-2">
          {["basico", "pro", "elite"].map((p) => {
            const m = PLAN_META[p];
            const activo = p === plan;
            return (
              <div key={p} className={`rounded-xl border px-2 py-2 text-center ${activo ? "border-2" : "border-slate-200"}`} style={activo ? { borderColor: m.dot, backgroundColor: m.tint } : {}} data-testid={`cr-comp-${p}`}>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: m.text }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.dot }} /> {m.label}
                </span>
                <p className="text-base font-extrabold text-slate-900">{Number(cfg.comisiones?.[p] ?? 0)}%</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color, strong, testid }) {
  return (
    <div className="flex items-center justify-between px-3 py-2" data-testid={testid}>
      <span className={`text-sm ${strong ? "font-bold text-slate-800" : "text-slate-500"}`}>{label}</span>
      <span className={`text-sm ${strong ? "font-extrabold" : "font-semibold"}`} style={{ color: color || "#0F172A" }}>{value}</span>
    </div>
  );
}
